/**
 * sImage "You May Also Like" — Lightweight Carousel + Card Enhancement
 * ─────────────────────────────────────────────────────────────────────
 * CHANGE LOG (Feb 2026): Complete rewrite.
 *
 * WHAT THIS DOES:
 *   1. Waits for the sImage app to render its product grid (#similar-products-list)
 *   2. Enhances each card with discount flags + formatted pricing (from /products/<handle>.js)
 *   3. Wraps the <ul> in a carousel shell with prev/next arrows and dot pagination
 *   4. Applies CSS scroll-snap so the list scrolls in pages:
 *        • Desktop (≥750px): 4 cards visible per page
 *        • Mobile  (<750px): 2 cards visible per page
 *   5. Supports touch swipe (native), arrow buttons, and dot clicks
 *
 * WHY NO EXTERNAL LIBRARY:
 *   CSS scroll-snap + a thin JS layer for arrows/dots is ~3 KB total.
 *   Flickity, Swiper, etc. add 30–80 KB. This keeps LCP and TBT low.
 *
 * FILES INVOLVED:
 *   • assets/simage-card.js  — this file (carousel + card enhancement logic)
 *   • assets/custom.css      — carousel layout, arrow, and dot styles
 *   Both are loaded globally via theme.liquid; the JS early-exits on non-product
 *   pages when #similar-products-list doesn't exist.
 */

(function() {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════
     SECTION 1: Card Enhancement — Discount Flags + Formatted Pricing
     ═══════════════════════════════════════════════════════════════════ */

  /** Map<string, Object> — avoids duplicate /products/<handle>.js fetches */
  var productCache = {};

  /** Format Shopify price-in-cents → "$1,495.00" */
  function formatMoney(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /** Extract the product handle from a Shopify product URL */
  function getProductHandle(url) {
    var match = url.match(/\/products\/([^?#/]+)/);
    return match ? match[1] : null;
  }

  /** Fetch product JSON with caching */
  function fetchProductData(handle) {
    if (productCache[handle]) {
      return Promise.resolve(productCache[handle]);
    }
    return fetch('/products/' + handle + '.js')
      .then(function(res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function(product) {
        productCache[handle] = product;
        return product;
      });
  }

  /**
   * enhanceCard(card, product)
   * Adds a discount flag badge and formatted price/savings to a single <li>.
   * Only touches elements INSIDE the card — no DOM changes outside it.
   */
  function enhanceCard(card, product) {
    if (!product.variants || !product.variants[0]) return;

    var variant = product.variants[0];
    var price = variant.price;
    var compareAtPrice = variant.compare_at_price;

    var imageContainer = card.querySelector('.tw-relative') || card.querySelector('a > div:first-child');
    var priceContainer = card.querySelector('.tw-mt-auto');
    if (!priceContainer) {
      // Fallback: find the wrapper around the original price span
      var priceSpan = card.querySelector('.tw-text-gray-900');
      priceContainer = priceSpan ? priceSpan.closest('div') : null;
    }
    if (!priceContainer) return;

 var pricingHTML;
    if (compareAtPrice && compareAtPrice > price) {
      var discountPercent = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);

      // ── Discount flag badge (top-right of image) ──
      if (imageContainer && !imageContainer.querySelector('.simage-discount-flag')) {
        var flag = document.createElement('div');
        flag.className = 'simage-discount-flag';
        flag.textContent = discountPercent + '% OFF';
        imageContainer.appendChild(flag);
      }

      pricingHTML =
        '<div class="simage-price-container">' +
          '<div class="simage-price-line">' +
            '<span class="simage-compare-price">' + formatMoney(compareAtPrice) + '</span>' +
            '<span class="simage-current-price">' + formatMoney(price) + '</span>' +
          '</div>' +
        '</div>';
    } else {
      pricingHTML =
        '<div class="simage-price-container">' +
          '<span class="simage-current-price">' + formatMoney(price) + '</span>' +
        '</div>';
    }

    // Only inject once
    if (!priceContainer.querySelector('.simage-price-container')) {
      var originalPrice = priceContainer.querySelector('span');
      if (originalPrice) originalPrice.style.display = 'none';
      priceContainer.insertAdjacentHTML('beforeend', pricingHTML);
    }
  }

  /**
   * enhanceAllCards(list)
   * Loops over every <li> in the product list and kicks off the fetch + enhance
   * pipeline for each unprocessed card.
   */
  function enhanceAllCards(list) {
    var cards = list.querySelectorAll('li.tw-group');
    cards.forEach(function(card) {
      if (card.dataset.simageEnhanced) return;
      card.dataset.simageEnhanced = 'processing';

      var link = card.querySelector('a[href*="/products/"]') || card.querySelector('a');
      if (!link) { card.dataset.simageEnhanced = 'failed'; return; }

      var handle = getProductHandle(link.href);
      if (!handle) { card.dataset.simageEnhanced = 'failed'; return; }

      fetchProductData(handle)
        .then(function(product) {
          enhanceCard(card, product);
          card.dataset.simageEnhanced = 'true';
        })
        .catch(function() {
          card.dataset.simageEnhanced = 'failed';
        });
    });
  }


  /* ═══════════════════════════════════════════════════════════════════
     SECTION 2: Carousel — Wrap, Arrows, Dots, Scroll-Snap Navigation
     ═══════════════════════════════════════════════════════════════════ */

  /**
   * How many cards are visible per "page" at the current viewport width.
   * Matches the CSS flex-basis values in custom.css.
   */
  function cardsPerPage() {
    return window.innerWidth < 750 ? 1 : 4;
  }

  /**
   * stripTailwindGrid(list)
   * Remove Tailwind grid-related classes from the <ul>'s className.
   * This prevents any stylesheet rule from re-establishing grid layout.
   * Called from forceFlexLayout and the attribute observer.
   */
  var _suppressAttrWatch = false;
  function stripTailwindGrid(list) {
    var orig = list.className;
    var cleaned = orig
      .split(/\s+/)
      .filter(function(cls) {
        // Remove tw-grid*, tw-gap*, and responsive variants
        return !/^(sm:|md:|lg:|xl:|2xl:)?tw-grid/.test(cls) &&
               !/^(sm:|md:|lg:|xl:|2xl:)?tw-gap/.test(cls);
      })
      .join(' ');
    if (cleaned !== orig) {
      _suppressAttrWatch = true;
      list.className = cleaned;
      // Release flag after microtask so the attribute observer ignores this change
      Promise.resolve().then(function() { _suppressAttrWatch = false; });
    }
  }

  /**
   * forceFlexLayout(list)
   * NUCLEAR OPTION: Strip grid classes AND set inline styles on the <ul>
   * to guarantee single-row flex layout. Inline !important always beats
   * stylesheet !important, which is the only way to override the sImage
   * app's Tailwind grid classes that it re-applies on render.
   */
  function forceFlexLayout(list) {
    // Step 1: Remove grid classes from the element
    stripTailwindGrid(list);

    // Step 2: Set inline styles (inline !important beats stylesheet !important)
    var styles = [
      'display:flex',
      'flex-wrap:nowrap',
      'overflow-x:auto',
      'scroll-snap-type:x mandatory',
      '-webkit-overflow-scrolling:touch',
      'scroll-behavior:smooth',
      'gap:12px',
      'padding:0 0 12px',
      'margin:0',
      'list-style:none',
      'scrollbar-width:none',
      // Kill any Tailwind grid properties
      'grid-template-columns:none',
      'grid-template-rows:none',
      'grid-auto-flow:unset'
    ];
    _suppressAttrWatch = true;
    list.style.cssText = styles.join(' !important;') + ' !important;';
    Promise.resolve().then(function() { _suppressAttrWatch = false; });
  }

  /**
   * watchListAttributes(list)
   * Sets up a MutationObserver on the <ul> itself for attribute changes.
   * If the sImage app (or its framework) re-sets the class or style
   * attribute, this fires and re-applies our flex layout immediately.
   * Returns the observer so it can be disconnected later if needed.
   */
  var _listAttrObserver = null;
  function watchListAttributes(list) {
    if (_listAttrObserver) _listAttrObserver.disconnect();
    _listAttrObserver = new MutationObserver(function() {
      if (_suppressAttrWatch) return;
      forceFlexLayout(list);
      forceCardSizing(list);
    });
    _listAttrObserver.observe(list, {
      attributes: true,
      attributeFilter: ['class', 'style']
    });
    return _listAttrObserver;
  }

  /**
   * forceCardSizing(list)
   * Sets inline width on each <li> card to match the CSS flex-basis values.
   * This ensures cards don't inherit Tailwind grid sizing.
   */
  function forceCardSizing(list) {
    var cards = list.querySelectorAll(':scope > li');
    var isMobile = window.innerWidth < 750;
    // Desktop: 4 cards in viewport (3 gaps × 12px = 36px)
    // Mobile:  1.5 cards visible (1 gap × 12px = 12px, divisor 1.5)
    var totalGap = isMobile ? 12 : 36;
    var divisor  = isMobile ? 1.5 : 4;
    var widthCalc = 'calc((100% - ' + totalGap + 'px) / ' + divisor + ')';
    for (var c = 0; c < cards.length; c++) {
      cards[c].style.cssText =
        'flex:0 0 ' + widthCalc + ' !important;' +
        'max-width:' + widthCalc + ' !important;' +
        'min-width:0 !important;' +
        'scroll-snap-align:start !important;' +
        'scroll-snap-stop:always !important;' +
        'flex-shrink:0 !important;' +
        'width:' + widthCalc + ' !important;';
    }
  }

  /**
   * initCarousel(list)
   * Wraps the <ul#similar-products-list> in a carousel shell and injects
   * prev/next arrow buttons + dot pagination (1 dot per card).
   * All scrolling is CSS-native (scroll-snap); this JS only drives the
   * buttons and active-dot state.
   */
  function initCarousel(list) {
    // Guard: don't double-init — check both the data attr AND whether
    // the list is already inside a .simage-carousel-wrap with arrows
    var existingWrap = list.closest('.simage-carousel-wrap');
    if (list.dataset.carouselInit && existingWrap && existingWrap.querySelector('.simage-carousel-btn')) return;
    list.dataset.carouselInit = 'true';

    var GAP = 12;
    var cards = list.querySelectorAll(':scope > li');
    var totalCards = cards.length;
    if (totalCards === 0) return;

    // ── 0. Force single-row layout via inline styles ────────────────
    forceFlexLayout(list);

    // ── 0b. Force card sizing inline (belt + suspenders with CSS) ───
    forceCardSizing(list);

    // ── 0c. Watch for attribute changes on the <ul> ─────────────────
    // If the sImage app re-sets class/style, we fight back immediately
    watchListAttributes(list);

    // ── 1. Wrap the <ul> in .simage-carousel-wrap ───────────────────
    // Remove any orphaned wrappers from previous app re-renders
    document.querySelectorAll('.simage-carousel-wrap').forEach(function(w) {
      if (!w.contains(list)) w.remove();
    });

    var wrap = list.closest('.simage-carousel-wrap');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'simage-carousel-wrap';
      list.parentNode.insertBefore(wrap, list);
      wrap.appendChild(list);
    }

    // Remove any old arrows/dots from a previous init
    wrap.querySelectorAll('.simage-carousel-btn, .simage-carousel-dots').forEach(function(el) {
      el.remove();
    });

    // ── 2. Create Prev / Next arrow buttons ─────────────────────────
    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'simage-carousel-btn simage-carousel-btn--prev';
    prevBtn.setAttribute('aria-label', 'Previous products');
    prevBtn.innerHTML = '<svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>';

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'simage-carousel-btn simage-carousel-btn--next';
    nextBtn.setAttribute('aria-label', 'Next products');
    nextBtn.innerHTML = '<svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg>';

    wrap.appendChild(prevBtn);
    wrap.appendChild(nextBtn);

    // ── 3. Create dot pagination — 1 dot per card ───────────────────
    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'simage-carousel-dots';
    wrap.appendChild(dotsWrap);

    function buildDots() {
      dotsWrap.innerHTML = '';
      for (var i = 0; i < totalCards; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'simage-carousel-dot';
        dot.setAttribute('aria-label', 'Go to product ' + (i + 1));
        dot.dataset.index = i;
        dotsWrap.appendChild(dot);
      }
      updateUI();
    }

    // ── 4. Scroll helpers ───────────────────────────────────────────

    /** Compute the scroll-left offset to bring card[index] to the start */
    function scrollLeftForCard(index) {
      if (index <= 0) return 0;
      var offset = 0;
      for (var i = 0; i < index && i < totalCards; i++) {
        offset += cards[i].offsetWidth + GAP;
      }
      return offset;
    }

    /** Return the index of the card whose left edge is closest to scrollLeft */
    function activeCardIndex() {
      var scrollLeft = list.scrollLeft;
      var cumLeft = 0;
      var closestIdx = 0;
      var closestDist = Infinity;
      for (var i = 0; i < totalCards; i++) {
        var dist = Math.abs(cumLeft - scrollLeft);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
        cumLeft += cards[i].offsetWidth + GAP;
      }
      return closestIdx;
    }

    /** Scroll so card[index] is at the left edge */
    function scrollToCard(index) {
      if (index < 0) index = 0;
      if (index >= totalCards) index = totalCards - 1;
      list.scrollTo({ left: scrollLeftForCard(index), behavior: 'smooth' });
    }

    /** Update arrow disabled state + highlight the active dot */
    function updateUI() {
      var idx = activeCardIndex();
      var maxStart = Math.max(0, totalCards - cardsPerPage());

      prevBtn.disabled = (idx <= 0);
      nextBtn.disabled = (idx >= maxStart);

      var dots = dotsWrap.querySelectorAll('.simage-carousel-dot');
      dots.forEach(function(dot) {
        dot.classList.toggle('active', parseInt(dot.dataset.index, 10) === idx);
      });
    }

    // ── 5. Event listeners ──────────────────────────────────────────

    // Arrows advance by one full "page" of cards
    prevBtn.addEventListener('click', function() {
      scrollToCard(activeCardIndex() - cardsPerPage());
    });

    nextBtn.addEventListener('click', function() {
      scrollToCard(activeCardIndex() + cardsPerPage());
    });

    // Dots scroll to that specific card
    dotsWrap.addEventListener('click', function(e) {
      var dot = e.target.closest('.simage-carousel-dot');
      if (dot) scrollToCard(parseInt(dot.dataset.index, 10));
    });

    // Update UI on scroll (native swipe or arrow click)
    var scrollTimer;
    list.addEventListener('scroll', function() {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateUI, 80);
    }, { passive: true });

    // Rebuild dots on resize (total cards doesn't change, but update UI)
    var resizeTimer;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function() {
        forceFlexLayout(list);
        forceCardSizing(list);
        updateUI();
      }, 150);
    });

    // ── 6. Initial state ────────────────────────────────────────────
    buildDots();
  }


  /* ═══════════════════════════════════════════════════════════════════
     SECTION 3: Initialization — Wait for App, Enhance, Carousel
     ═══════════════════════════════════════════════════════════════════

     The sImage app can re-render the product grid AFTER we've already
     wrapped it in a carousel. When this happens the <ul> innerHTML is
     replaced, the carousel wrapper may get orphaned, and the user sees
     the raw grid flash back.

     Strategy:
       1. Use a persistent MutationObserver on the app's container.
       2. Every time the observer fires and #similar-products-list has
          <li> children, re-run processSimageList (enhance + carousel).
       3. Both enhanceAllCards and initCarousel are idempotent — they
          check for prior work before touching the DOM.
     ═══════════════════════════════════════════════════════════════════ */

  /**
   * processSimageList(list)
   * Called once the #similar-products-list is found with child <li> elements.
   * Kicks off card enhancement, then wraps the list in a carousel.
   */
  function processSimageList(list) {
    // ── NUCLEAR: Force flex layout FIRST, before anything else ──────
    // This must run before enhanceAllCards so the layout is single-row
    // immediately, preventing any flash of 2-row grid.
    forceFlexLayout(list);

    // Enhance cards (discount flags + prices)
    enhanceAllCards(list);
    // Build the carousel shell around the list
    initCarousel(list);
  }

  /** Debounce helper */
  function debounce(func, wait) {
    var timeout;
    return function() {
      var ctx = this, args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function() { func.apply(ctx, args); }, wait);
    };
  }

  /**
   * init()
   * Entry point. Uses a persistent MutationObserver that survives the
   * sImage app's re-renders. The observer keeps watching until 30s
   * after the LAST successful carousel init (not 10s from page load).
   */
  function init() {
    /** Track how many times we've processed so we know if app re-rendered */
    var processCount = 0;
    var disconnectTimer = null;
    var observer = null;

    function tryProcess() {
      var list = document.querySelector('#similar-products-list');
      if (!list) return;

      var items = list.querySelectorAll(':scope > li');
      if (items.length === 0) return;

      // Always re-force flex layout — the sImage app may have re-applied
      // its Tailwind grid classes since last check
      forceFlexLayout(list);
      forceCardSizing(list);

      // Check if the carousel is already intact (list inside wrapper, arrows present)
      var wrap = list.closest('.simage-carousel-wrap');
      var hasArrows = wrap && wrap.querySelector('.simage-carousel-btn');
      var allEnhanced = !list.querySelector('li.tw-group:not([data-simage-enhanced])');

      // Only re-process if carousel is missing OR cards are un-enhanced
      if (hasArrows && allEnhanced) return;

      processCount++;
      processSimageList(list);

      // Reset the auto-disconnect timer — give 30s after last init
      // so we catch any late re-renders
      if (disconnectTimer) clearTimeout(disconnectTimer);
      disconnectTimer = setTimeout(function() {
        if (observer) observer.disconnect();
      }, 30000);
    }

    // Retry at 0ms, 500ms, 1s, 2s, 4s (covers slow app renders)
    [0, 500, 1000, 2000, 4000].forEach(function(delay) {
      setTimeout(tryProcess, delay);
    });

    // Persistent MutationObserver — watches for the sImage app to
    // render or RE-render #similar-products-list
    var debouncedTry = debounce(tryProcess, 200);
    setTimeout(function() {
      var target = document.querySelector('[id*="simage_similar_items"]') || document.body;
      observer = new MutationObserver(debouncedTry);
      observer.observe(target, { childList: true, subtree: true });
      // Safety net: disconnect after 30s if we never found anything
      disconnectTimer = setTimeout(function() {
        if (observer) observer.disconnect();
      }, 30000);
    }, 200);
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
